import { CardBody, Empty, SlateViewer, isEmptySlateValue } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EventTypeOptionResource } from "../../../../shared/types/eventType";
import { Address } from "../../../../shared/ui/Address";
import { DetailsPageInfoField } from "../../../../shared/ui/DetailsPageInfoField";
import { EventDetailResource } from "../../shared/types";

const InformationGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	grid-gap: ${({ theme }) => theme.spacing(2)};

	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-columns: 1fr 1fr 1fr;
	}
`;

const StyledCardBody = styled(CardBody)`
	display: flex;
	flex-direction: column;
	grid-gap: ${({ theme }) => theme.spacing(5)};
`;

interface Props {
	event: EventDetailResource;
}

const shouldShowEmptyMessage = (eventType: EventTypeOptionResource): boolean => {
	return (
		eventType.participationPermissionRequirement === "prohibited" &&
		eventType.formatRequirement === "prohibited" &&
		eventType.eventCategoryRequirement === "prohibited" &&
		eventType.industryRequirement === "prohibited" &&
		eventType.targetGroupRequirement === "prohibited" &&
		eventType.regionRequirement === "prohibited" &&
		eventType.primaryOrganizationalUnitRequirement === "prohibited" &&
		eventType.organizationalUnitRequirement === "prohibited" &&
		eventType.tagRequirement === "prohibited" &&
		eventType.preferenceRequirement === "prohibited" &&
		eventType.additionalInfoRequirement === "prohibited" &&
		eventType.eventGoalRequirement === "prohibited"
	);
};

export const EventDetailsCard: FC<Props> = ({ event }) => {
	const { t } = useTranslation();

	return (
		<>
			<StyledCardBody>
				{shouldShowEmptyMessage(event.eventType) && <Empty>{t("No data available.")}</Empty>}
				<InformationGrid>
					{event.eventType.eventGoalRequirement !== "prohibited" && (
						<DetailsPageInfoField title={t("Event goal")} value={event.eventGoal || "-"} />
					)}
					{event.eventType.participationPermissionRequirement !== "prohibited" && (
						<DetailsPageInfoField
							title={t("Participation permission")}
							value={event.isInviteOnly !== null ? (event.isInviteOnly ? t("Only with invitation") : t("All")) : "-"}
						/>
					)}
					{event.eventType.formatRequirement !== "prohibited" && (
						<DetailsPageInfoField title={t("Format")} value={event.format ? t(event.format) : "-"} />
					)}
					{event.eventType.eventCategoryRequirement !== "prohibited" && (
						<DetailsPageInfoField title={t("Category")} value={event.category ? event.category.name : "-"} />
					)}
					{event.eventType.industryRequirement !== "prohibited" && (
						<DetailsPageInfoField
							title={t("Industry", { count: event.industries.length })}
							value={event.industries.length > 0 ? event.industries.map((i) => i.name).join(", ") : "-"}
						/>
					)}
					{event.eventType.targetGroupRequirement !== "prohibited" && (
						<DetailsPageInfoField title={t("Target group")} value={event.targetGroup ? event.targetGroup.name : "-"} />
					)}
					{event.eventType.regionRequirement !== "prohibited" && (
						<DetailsPageInfoField title={t("Region")} value={event.region ? event.region.name : "-"} />
					)}
					{event.eventType.primaryOrganizationalUnitRequirement !== "prohibited" && (
						<DetailsPageInfoField
							title={t("Primary organizational unit")}
							value={event.primaryOrganizationalUnit ? event.primaryOrganizationalUnit.name : "-"}
						/>
					)}
					{event.eventType.organizationalUnitRequirement !== "prohibited" && (
						<DetailsPageInfoField
							title={t("Organizational unit", { count: event.organizationalUnits.length })}
							value={
								event.organizationalUnits.length > 0 ? event.organizationalUnits.map((o) => o.name).join(", ") : "-"
							}
						/>
					)}
					{event.eventType.tagRequirement !== "prohibited" && (
						<DetailsPageInfoField
							title={t("Tag", { count: event.tags.length })}
							value={event.tags.length ? event.tags.map((tag) => tag.name).join(", ") : "-"}
						/>
					)}
					{event.eventType.preferenceRequirement !== "prohibited" && (
						<DetailsPageInfoField
							title={t("Preferences")}
							value={event.preferences.length ? event.preferences.map((preference) => preference.name).join(", ") : "-"}
						/>
					)}
					{event.eventType.addressRequirement !== "prohibited" && (
						<DetailsPageInfoField
							title={t("Address")}
							value={event.address ? <Address address={event.address} /> : "-"}
						/>
					)}
				</InformationGrid>
				{event.eventType.additionalInfoRequirement !== "prohibited" && event.additionalInfo && (
					<DetailsPageInfoField
						title={t("Additional information")}
						value={isEmptySlateValue(event.additionalInfo) ? "-" : <SlateViewer content={event.additionalInfo} />}
					/>
				)}
			</StyledCardBody>
		</>
	);
};
